/**
 * @fileoverview Helpers for the ops console's runtime flags.
 *
 * The backend serves every flag value as a STRING (or null) regardless of kind,
 * because the underlying layers it resolves through — a DynamoDB override row
 * and an environment variable — are both string-typed. Truthiness therefore has
 * to be interpreted the same way on both sides; `TRUTHY` mirrors the backend's
 * `services/runtime_config._TRUTHY` set exactly.
 */

/** Values the backend accepts as "on" for a `kind: "bool"` flag. */
export const TRUTHY = ["true", "1", "yes", "on"];

/** True when a bool flag's string value reads as on. */
export function isFlagOn(value) {
  if (value == null) return false;
  return TRUTHY.includes(String(value).trim().toLowerCase());
}

/**
 * Group flags into `[{ group, flags }]`, preserving the order each group first
 * appears in the server's list (which follows the registry's declaration order,
 * so the console reads in the order the backend author intended).
 */
export function groupFlags(flags) {
  const order = [];
  const byGroup = new Map();
  for (const flag of flags || []) {
    const group = flag.group || "Other";
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      order.push(group);
    }
    byGroup.get(group).push(flag);
  }
  return order.map((group) => ({ group, flags: byGroup.get(group) }));
}

/** Human-readable rendering of a flag value for display (never for editing). */
export function displayValue(value) {
  if (value == null || value === "") return "not set";
  return String(value);
}

/**
 * Where the effective value came from, as a sentence fragment. `db` means an
 * operator override is live; anything else means the deploy-time environment
 * (or the registry default) is still in charge.
 */
export function sourceLabel(source) {
  if (source === "db") return "Overridden here";
  if (source === "env") return "From the environment";
  return "Registry default";
}
